import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  getGetUserQueryOptions,
  useSendInviteEmail,
  useInviteUser,
} from "@/requests/gen/react-query/user";
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import snakeToCamel from "@/lib/snake-to-camel";
import {
  Group,
  Hash,
  House,
  IdCard,
  Mail,
  Phone,
  Captions,
} from "lucide-react";
import { UserCrumbs } from "../-crumbs";
import { toast } from "sonner";
import parsePhoneNumber from "libphonenumber-js";
import DisableUserDropdown from "./-disable-user-dropdown";
import { UserRoleEnum } from "@/requests/gen/react-query/fastAPI.schemas";

export const Route = createFileRoute("/_private/_admin/user/$userId/")({
  loader: ({ context: { queryClient }, params: { userId } }) =>
    queryClient.ensureQueryData(getGetUserQueryOptions(userId)),
  component: UserReviewPage,
});

function UserReviewPage() {
  const { userId } = Route.useParams();
  const [emailSuccess, setEmailSuccess] = useState(false);
  const {
    data: user,
    isLoading,
    refetch,
  } = useSuspenseQuery(suspenseOptions(getGetUserQueryOptions(userId)));

  const invite = useInviteUser();

  const sendEmail = useSendInviteEmail({
    mutation: {
      onSuccess: () => {
        toast.success("Invite email sent successfully.");
        setEmailSuccess(true);
      },
      onError: (error) => {
        console.error(error);
        toast.error("Could not send invite email.");
        setEmailSuccess(false);
      },
    },
  });

  const handleInvite = () => {
    // If invite link is already generated, copy it
    if (invite.data?.Link) {
      navigator.clipboard.writeText(invite.data.Link);
      toast.success("Invite link copied to your clipboard.");
      return;
    }
    // If user has email, and invite is not yet generated, create invite
    if (user?.email) {
      invite.mutate(
        { email: user.email },
        {
          onSuccess: (data) => {
            navigator.clipboard.writeText(data.Link);
            toast.success("Invite link copied to your clipboard.");
          },
          onError: (error) => {
            console.error(error);
            toast.error("Error getting invite link.");
          },
        },
      );
    }
  };

  const handleSendEmail = () => {
    // Check if user exists
    if (!user) return;
    // Check if email has already been sent
    if (emailSuccess) return;
    // Send invite email
    sendEmail.mutate({ userEmail: user.email });
  };

  return (
    <div>
      <title>{`${user?.first_name} ${user?.last_name} - Legrande Health Portal`}</title>
      <UserCrumbs isSalesAdvisor={user?.role === UserRoleEnum.sales_advisor}>
        {user && (
          <DisableUserDropdown
            refetch={refetch}
            user={user}
            isDeactivated={user.is_deleted}
          />
        )}
      </UserCrumbs>
      {/* User form */}
      <Card>
        <CardHeader>
          <CardTitle>
            {
              // Show a loading state if the data is still loading
              isLoading ? "Loading..." : "Review"
            }
          </CardTitle>
          <CardDescription>
            Review user details before sending invite.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {user && (
            <div>
              <h4 className="text-xl font-bold mb-4">
                {user.first_name} {user.last_name}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Col 1 */}
                <div>
                  <p className="flex items-center gap-2 font-semibold">
                    <Captions className="inline size-5" />
                    Title
                  </p>
                  <p className="ml-8 text-muted-foreground mb-4">
                    {user.title ?? ""}
                  </p>
                  <p className="flex items-center gap-2 font-semibold">
                    <Mail className="inline size-5" />
                    Email
                  </p>
                  <p className="ml-8 text-muted-foreground mb-4">
                    {user.email}
                  </p>

                  <p className="flex items-center gap-2 font-semibold">
                    <Phone className="inline size-5" />
                    Phone
                  </p>
                  <p className="ml-8 text-muted-foreground mb-4">
                    {parsePhoneNumber(user.phone ?? "")?.formatNational()}
                  </p>

                  <p className="flex items-center gap-2 font-semibold">
                    <Hash className="inline size-5" />
                    NPI Number
                  </p>
                  <p className="ml-8 text-muted-foreground mb-4">
                    {user.npi_number}
                  </p>

                  <p className="flex items-center gap-2 font-semibold">
                    <IdCard className="inline size-5" />
                    State License Number
                  </p>
                  <p className="ml-8 text-muted-foreground mb-4">
                    {user.state_license_number}
                  </p>

                  <p className="flex items-center gap-2 font-semibold">
                    <Group className="inline size-5" />
                    Role
                  </p>
                  <p className="ml-8 text-muted-foreground mb-4">
                    {snakeToCamel(user.role)}
                  </p>
                </div>

                {/* Col 2 */}
                <div>
                  <p className="flex items-center gap-2 font-bold text-xl">
                    <House className="inline size-5" />
                    Entities
                  </p>
                  {user.user_entities.map((user_entity) => (
                    <div className="ml-8" key={user_entity.entity_id}>
                      <p>{user_entity.entity.name}</p>
                      {user_entity.commission && (
                        <p key={user_entity.commission?.user_entity_id}>
                          {`${user_entity.commission?.sales_advisor_type} Advisor - ${user_entity.commission.commission_rate.toFixed(2)}% Commission`}
                        </p>
                      )}

                      {/* Permissions */}
                      <ul className="ml-4 list-disc mb-4">
                        {user_entity.permissions?.map((permission) => (
                          <li key={permission.permission}>
                            {snakeToCamel(permission.permission)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end mt-5 gap-4">
                {user.is_deleted ? (
                  <Button disabled variant="outline">
                    Edit User
                  </Button>
                ) : (
                  <Button variant="outline" asChild>
                    <Link to="/user/$userId/edit" params={{ userId }}>
                      Edit User
                    </Link>
                  </Button>
                )}

                <div className="flex gap-4">
                  <Button
                    disabled={user.is_deleted}
                    onClick={handleInvite}
                    loading={invite.isPending}
                    variant="outline"
                  >
                    Copy Invite
                  </Button>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={handleSendEmail}
                        disabled={emailSuccess || user.is_deleted}
                        loading={sendEmail.isPending}
                      >
                        Send Invite Email
                      </Button>
                    </TooltipTrigger>
                    {emailSuccess && (
                      <TooltipContent>
                        <p>Invite email already sent!</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
